// Date / duration / number formatting. Intl-only, no library deps.
// All helpers tolerate null/undefined and return a placeholder dash so
// callers don't have to guard at every site.

const dateTimeFmt = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const dateOnlyFmt = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

const timeOnlyFmt = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

const num2dpFmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const usdFmt = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const compactFmt = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const dashStr = '—';

const dayNameFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const monthDayFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

function asDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value: Date | string | null | undefined): string {
  const d = asDate(value);
  return d ? dateTimeFmt.format(d) : dashStr;
}

export function formatDate(value: Date | string | null | undefined): string {
  const d = asDate(value);
  return d ? dateOnlyFmt.format(d) : dashStr;
}

/** "3:45 PM" — short time-of-day for chat timestamps. */
export function formatTime(value: Date | string | null | undefined): string {
  const d = asDate(value);
  return d ? timeOnlyFmt.format(d) : dashStr;
}

/** "2m 14s" / "184 ms" / "—". Accepts a Date pair OR pre-computed milliseconds. */
export function formatDuration(
  startOrMs: Date | string | number | null | undefined,
  end?: Date | string | null | undefined,
): string {
  let ms: number | null = null;
  if (typeof startOrMs === 'number') {
    ms = startOrMs;
  } else {
    const start = asDate(startOrMs as Date | string | null | undefined);
    const finish = asDate(end ?? null);
    if (!start) return dashStr;
    ms = (finish?.getTime() ?? Date.now()) - start.getTime();
  }
  if (ms === null || ms < 0 || !Number.isFinite(ms)) return dashStr;
  if (ms < 1000) return `${ms.toFixed(0)} ms`;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/**
 * How long ago something started, as a spelled-out age ("5 days", "3 hours",
 * "1 minute"). Distinct from {@link formatRelative}, which is a terse pointer
 * to an instant ("5d ago", "in 4h"); this reads as a DURATION, for "how long
 * has this been running". Pluralized, single-unit, always non-negative — a
 * future timestamp (clock skew) floors at "just now". Null → dash.
 */
export function formatAge(value: Date | string | null | undefined): string {
  const d = asDate(value);
  if (!d) return dashStr;
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  const units: [limit: number, secs: number, name: string][] = [
    [3600, 60, 'minute'],
    [86_400, 3600, 'hour'],
    [2_592_000, 86_400, 'day'],
    [31_536_000, 2_592_000, 'month'],
    [Infinity, 31_536_000, 'year'],
  ];
  for (const [limit, secs, name] of units) {
    if (sec < limit) {
      const n = Math.floor(sec / secs);
      return `${n} ${name}${n === 1 ? '' : 's'}`;
    }
  }
  return dashStr;
}

/** Relative time ("3m ago", "2d ago", "in 4h"). Null → dash. */
export function formatRelative(value: Date | string | null | undefined): string {
  const d = asDate(value);
  if (!d) return dashStr;
  const ms = d.getTime() - Date.now();
  const abs = Math.abs(ms);
  const sec = Math.round(abs / 1000);
  let n: number;
  let unit: string;
  if (sec < 60) {
    n = sec;
    unit = 's';
  } else if (sec < 3600) {
    n = Math.round(sec / 60);
    unit = 'm';
  } else if (sec < 86400) {
    n = Math.round(sec / 3600);
    unit = 'h';
  } else {
    n = Math.round(sec / 86400);
    unit = 'd';
  }
  return ms < 0 ? `${n}${unit} ago` : `in ${n}${unit}`;
}

/** Smart timestamp for chat thread sidebars.
 *  < 1 min  → "just now"
 *  < 1 hour → "5m ago"
 *  today    → "2:30 PM"
 *  yesterday → "Yesterday"
 *  this week → "Mon"
 *  older    → "Jul 15"
 */
export function formatChatTime(value: Date | string | null | undefined): string {
  const d = asDate(value);
  if (!d) return dashStr;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86400_000);
  const weekStart = new Date(todayStart.getTime() - 6 * 86400_000);

  if (d >= todayStart) return timeOnlyFmt.format(d);
  if (d >= yesterdayStart) return 'Yesterday';
  if (d >= weekStart) return dayNameFmt.format(d);
  return monthDayFmt.format(d);
}

export function formatTokens(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dashStr;
  return compactFmt.format(n);
}

export function formatUsd(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return dashStr;
  const num = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(num)) return dashStr;
  return usdFmt.format(num);
}

export function formatPercent(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dashStr;
  return `${num2dpFmt.format(n * 100)}%`;
}
